import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, House, ArrowSquareOut } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import NotFound from "@/pages/NotFound";

export default function CanvaPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not-found | error

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setPage(null);
    (async () => {
      try {
        const { data } = await api.get(`/pages/${slug}`);
        if (cancelled) return;
        setPage(data);
        setStatus("ok");
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) setStatus("not-found");
        else setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center px-5"
        data-testid="canva-page-loading"
      >
        <div className="font-display text-xl uppercase tracking-widest text-ot-fg/60">
          Loading…
        </div>
      </div>
    );
  }

  if (status === "not-found") return <NotFound />;

  if (status === "error" || !page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-5 py-20">
        <div className="nb-card p-8 max-w-md text-center">
          <h1 className="font-display text-3xl uppercase tracking-tighter">
            Something went wrong
          </h1>
          <p className="mt-3 text-ot-fg/70">
            We couldn't load this page. Please try again in a moment.
          </p>
          <Link to="/" className="nb-btn mt-6">
            <House weight="bold" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="canva-page">
      <Seo
        title={page.title}
        description={page.description || `${page.title} — presentation by OneTec`}
        path={`/p/${page.slug}`}
        type="article"
      />

      {/* Header / hero strip — keeps the OneTec Neo-Brutalist identity */}
      <section className="border-b-2 border-ot-fg bg-ot-bg">
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-20 md:pt-28 pb-10 md:pb-14">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-xs uppercase tracking-widest font-bold text-ot-indigo"
          >
            Presentation
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95] mt-3"
          >
            {page.title}
          </motion.h1>
          {page.description && (
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-5 text-base md:text-lg text-ot-fg/70 max-w-2xl"
            >
              {page.description}
            </motion.p>
          )}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-6 flex flex-wrap gap-3 items-center"
          >
            <a
              href={page.canva_url}
              target="_blank"
              rel="noopener noreferrer"
              className="nb-btn nb-btn-ghost text-sm"
              data-testid="canva-open-external"
            >
              Open in new tab <ArrowSquareOut weight="bold" />
            </a>
            <Link to="/contact" className="nb-btn text-sm" data-testid="canva-contact-cta">
              Get in touch <ArrowUpRight weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Canva iframe — Neo-Brutalist framed embed */}
      <section className="px-5 md:px-10 py-10 md:py-16 bg-ot-bg">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative border-2 border-ot-fg shadow-[12px_12px_0px_var(--ot-indigo)] bg-white"
            data-testid="canva-embed-frame"
          >
            <div
              className="relative w-full"
              style={{ paddingTop: "56.25%" /* 16:9 */ }}
            >
              <iframe
                src={page.canva_url}
                title={page.title}
                loading="lazy"
                allow="fullscreen"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                data-testid="canva-iframe"
              />
            </div>
          </motion.div>
          <p className="mt-4 text-xs text-ot-fg/50 text-center">
            If the presentation doesn't load,{" "}
            <a
              href={page.canva_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-ot-indigo"
            >
              open it in a new tab
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
